import { forwardRef, type PropsWithChildren } from "react";

import { styled } from "@linaria/react";

import { getCssSizeValue } from "../utils/linaria";

export enum FlexDirection {
  ROW = "row",
  COLUMN = "column",
  ROW_REVERSE = "row-reverse",
  COLUMN_REVERSE = "column-reverse",
}

export enum AlignItems {
  START = "start",
  CENTER = "center",
  END = "end",
  BASELINE = "baseline",
  STRETCH = "stretch",
}

export enum JustifyContent {
  START = "start",
  CENTER = "center",
  END = "end",
  SPACE_BETWEEN = "space-between",
  SPACE_AROUND = "space-around",
  SPACE_EVENLY = "space-evenly",
}

export enum FlexWrap {
  NOWRAP = "nowrap",
  WRAP = "wrap",
  WRAP_REVERSE = "wrap-reverse",
}

export enum FlexGap {
  NONE = "0",
  XXSMALL = "2px",
  XSMALL = "4px",
  SMALL = "8px",
  MEDIUM = "12px",
  LARGE = "16px",
  XLARGE = "24px",
  XXLARGE = "32px",
}

interface FlexWrapperProps {
  /**
   * Sets the direction of the flex container
   * - ROW: Items are placed in a row (default)
   * - COLUMN: Items are placed in a column
   * - ROW_REVERSE: Items are placed in a row, from right to left
   * - COLUMN_REVERSE: Items are placed in a column, from bottom to top
   */
  direction?: FlexDirection;

  /**
   * Defines how flex items are aligned along the cross axis
   * - START: Items are aligned at the start (default)
   * - CENTER: Items are centered
   * - END: Items are aligned at the end
   * - BASELINE: Items are aligned at their baselines
   * - STRETCH: Items are stretched to fill the container
   */
  alignItems?: AlignItems;

  /**
   * Defines how flex items are aligned along the main axis
   * - START: Items are aligned at the start (default)
   * - CENTER: Items are centered
   * - END: Items are aligned at the end
   * - SPACE_BETWEEN: Items are evenly distributed with equal space between them
   * - SPACE_AROUND: Items are evenly distributed with equal space around them
   * - SPACE_EVENLY: Items are evenly distributed with equal space between and around them
   */
  justifyContent?: JustifyContent;

  /**
   * Controls whether flex items should wrap when there isn't enough space
   * - NOWRAP: Items will not wrap (default)
   * - WRAP: Items will wrap onto multiple lines if needed
   * - WRAP_REVERSE: Items will wrap onto multiple lines in reverse order
   */
  wrap?: FlexWrap;

  /**
   * Sets the gap between flex items in pixels or string (for other units)
   */
  gap?: number | string;

  /**
   * Sets the width of the flex container in pixels
   */
  width?: number | string;

  /**
   * Sets the height of the flex container in pixels
   */
  height?: number | string;

  /**
   * Sets the minimum width of the flex container in pixels
   */
  minWidth?: number | string;

  /**
   * Sets the maximum width of the flex container in pixels
   */
  maxWidth?: number | string;

  /**
   * Sets the minimum height of the flex container in pixels
   */
  minHeight?: number | string;

  /**
   * Sets the maximum height of the flex container in pixels
   */
  maxHeight?: number | string;

  /**
   * When true, the container will fill its parent's width (equivalent to width: 100%)
   */
  fillWidth?: boolean;

  /**
   * When true, the container will fill its parent's height (equivalent to height: 100%)
   */
  fillHeight?: boolean;

  /**
   * Controls how content that overflows the container should be handled
   * Default is 'visible'
   */
  overflow?: React.CSSProperties["overflow"];

  /**
   * Sets the padding inside the container
   * Can be a number (for pixels) or a string (for other units)
   */
  padding?: React.CSSProperties["padding"];

  /**
   * Sets the margin inside the container
   * Can be a number (for pixels) or a string (for other units)
   */
  margin?: React.CSSProperties["margin"];

  /**
   * Optional click handler for the flex container
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * Optional mousedown handler for the flex container
   */
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * Optional mouseenter handler for the flex container
   */
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * Optional mouseleave handler for the flex container
   */
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * Optional scroll handler for the flex container
   */
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
}

const StyledFlexWrapper = styled.div<FlexWrapperProps>`
  display: flex;
  box-sizing: border-box;

  flex-direction: ${({ direction = FlexDirection.ROW }) => direction};
  align-items: ${({ alignItems = AlignItems.START }) => alignItems};
  justify-content: ${({ justifyContent = JustifyContent.START }) => justifyContent};

  gap: ${({ gap }) => getCssSizeValue(gap)};
  flex-wrap: ${({ wrap = FlexWrap.NOWRAP }) => wrap};

  width: ${({ width, fillWidth }) => {
    if (fillWidth) return "100%";
    if (width) return getCssSizeValue(width, "0");
    return "auto";
  }};
  height: ${({ height, fillHeight }) => {
    if (fillHeight) return "100%";
    if (height) return getCssSizeValue(height);
    return "auto";
  }};
  min-width: ${({ minWidth }) => (minWidth ? getCssSizeValue(minWidth) : "auto")};
  max-width: ${({ maxWidth }) => (maxWidth ? getCssSizeValue(maxWidth) : "none")};
  min-height: ${({ minHeight }) => (minHeight ? getCssSizeValue(minHeight) : "auto")};
  max-height: ${({ maxHeight }) => (maxHeight ? getCssSizeValue(maxHeight) : "none")};

  overflow: ${({ overflow = "visible" }) => overflow};
  padding: ${({ padding = "0" }) => padding};
  margin: ${({ margin = "0" }) => margin};
  overscroll-behavior: auto;

  cursor: ${({ onClick }) => (onClick ? "pointer" : "inherit")};
`;

const FlexWrapper = forwardRef<HTMLDivElement, PropsWithChildren<FlexWrapperProps>>(
  (
    {
      children,
      direction,
      alignItems,
      justifyContent,
      wrap,
      gap,
      width,
      height,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      fillWidth = false,
      fillHeight = false,
      overflow,
      padding,
      margin,
      onClick,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      onScroll,
    },
    ref,
  ) => {
    return (
      <StyledFlexWrapper
        ref={ref}
        direction={direction}
        alignItems={alignItems}
        justifyContent={justifyContent}
        wrap={wrap}
        gap={gap}
        width={width}
        height={height}
        minWidth={minWidth}
        maxWidth={maxWidth}
        minHeight={minHeight}
        maxHeight={maxHeight}
        fillWidth={fillWidth}
        fillHeight={fillHeight}
        overflow={overflow}
        padding={padding}
        margin={margin}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onScroll={onScroll}
      >
        {children}
      </StyledFlexWrapper>
    );
  },
);

FlexWrapper.displayName = "FlexWrapper";

export default FlexWrapper;
